import { fg } from '@atlaskit/platform-feature-flags/fg';

// Cap the length of any single allow-listed field we record, as a defensive
// guard against unexpectedly large values.
const MAX_ERROR_FIELD_LENGTH = 200;

// ConvoAI puts a dotted key like `convoai.rest.forbidden` in `errors[0]`. Only keep
// values in that shape so free text, tokens, or hostnames are never recorded.
const MAX_CONVOAI_ERROR_KEY_LENGTH = 100;
const CONVOAI_ERROR_KEY_PATTERN = /^convoai(\.[A-Za-z0-9_-]+){1,12}$/;
const OTHER_ERROR_KEY = 'other';

const getConvoAIErrorKey = (errors: unknown): string | undefined => {
	if (!Array.isArray(errors) || typeof errors[0] !== 'string' || errors[0].length === 0) {
		return undefined;
	}
	const candidate = errors[0];
	return candidate.length <= MAX_CONVOAI_ERROR_KEY_LENGTH &&
		CONVOAI_ERROR_KEY_PATTERN.test(candidate)
		? candidate
		: OTHER_ERROR_KEY;
};

/**
 * Extract structured, allow-listed error details from a backend error payload.
 *
 * IMPORTANT (privacy): backend error bodies can echo user-generated content
 * (e.g. the user's search query reflected in a validation message, or an
 * unstructured HTML/stack-trace body). To avoid recording UGC into analytics we
 * do NOT record the raw body. We only extract a small set of known,
 * system-generated fields (error key/type and status code). The full body
 * remains retrievable from backend/gateway logs via the trace id.
 *
 * When `kd_sain_access_error_category` is on and there's no `key`/`type`/`code`,
 * the key falls back to ConvoAI's `errors[0]`. A missing `errors[0]` records no
 * key; a value that isn't a ConvoAI key records `other`.
 *
 * Returns a compact, non-UGC string suitable for the `details` field, or
 * undefined when nothing safe could be extracted.
 */
export function extractStructuredErrorDetails(rawBody: string | undefined): string | undefined {
	if (!rawBody) {
		return undefined;
	}
	try {
		const parsed = JSON.parse(rawBody);
		const errorObj = (parsed?.error ?? parsed) as Record<string, unknown> | undefined;
		if (!errorObj || typeof errorObj !== 'object') {
			return undefined;
		}
		const clip = (value: unknown): string | undefined =>
			typeof value === 'string' && value.length > 0
				? value.slice(0, MAX_ERROR_FIELD_LENGTH)
				: typeof value === 'number'
					? String(value)
					: undefined;
		// Allow-list of non-UGC identifiers only. Deliberately excludes free-form
		// message text, which may contain user-generated content.
		const legacyKey = clip(errorObj.key ?? errorObj.type ?? errorObj.code);
		const key = fg('kd_sain_access_error_category')
			? (legacyKey ?? getConvoAIErrorKey(errorObj.errors))
			: legacyKey;
		const statusCode = clip(errorObj.statusCode ?? errorObj.status);
		const safe: Record<string, string> = {};
		if (key) {
			safe.key = key;
		}
		if (statusCode) {
			safe.statusCode = statusCode;
		}
		return Object.keys(safe).length > 0 ? JSON.stringify(safe) : undefined;
	} catch {
		// Non-JSON body (e.g. HTML error page) - do not record it, to avoid UGC.
		return undefined;
	}
}
